import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Bot, 
  Trophy, 
  Star,
  Crown,
  Target,
  Zap,
  ChevronRight,
  Brain,
  Gamepad2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface BotChallengeSystemProps {
  userProgress?: {
    botsDefeated: number;
    totalBots: number;
    currentBotLevel: number;
    hatchGamesPlayed: number;
  };
  onShowBotChallenge?: (botNames?: string[]) => void;
  onStartHatchEvolution?: () => void;
}

const botChallengeSteps = [
  {
    id: 'rookie-bots',
    title: 'Challenge Rookie Bots',
    description: 'Start your journey against friendly AI opponents!',
    icon: Bot,
    color: 'text-teen-green',
    bgColor: 'bg-teen-green/10',
    borderColor: 'border-teen-green/30',
    shadowColor: 'shadow-teen-green',
    route: '/',
    action: 'bot-challenge',
    emoji: '🤖',
    level: 1,
    reward: 'Bot Challenger Badge! 🏅',
    bots: ['RookieBot', 'BeginnerBot']
  },
  {
    id: 'hatch-evolution',
    title: 'Try Hatch Evolution',
    description: 'Experience revolutionary chess with evolving pieces!',
    icon: Zap,
    color: 'text-teen-blue',
    bgColor: 'bg-teen-blue/10',
    borderColor: 'border-teen-blue/30',
    shadowColor: 'shadow-teen-blue',
    route: '/',
    action: 'hatch-evolution',
    emoji: '⚡',
    level: 1,
    reward: 'Evolution Pioneer Badge! 🧬'
  },
  {
    id: 'advanced-bots',
    title: 'Face Advanced Bots',
    description: 'Test your skills against smarter AI opponents!',
    icon: Brain,
    color: 'text-teen-purple',
    bgColor: 'bg-teen-purple/10',
    borderColor: 'border-teen-purple/30',
    shadowColor: 'shadow-teen-purple',
    route: '/',
    action: 'bot-challenge',
    emoji: '🧠',
    level: 2,
    reward: 'AI Conqueror Badge! 🎯',
    bots: ['ChillChamp', 'ClubPlayer']
  },
  {
    id: 'master-bots',
    title: 'Defeat Master Bots',
    description: 'Challenge the most powerful AI opponents!',
    icon: Crown,
    color: 'text-teen-gold',
    bgColor: 'bg-teen-gold/10',
    borderColor: 'border-teen-gold/30',
    shadowColor: 'shadow-teen-gold',
    route: '/',
    action: 'bot-challenge',
    emoji: '👑',
    level: 3,
    reward: 'Bot Master Badge! 🏆',
    bots: ['BrainBuster', 'ExpertBot', 'MegaMind', 'MasterBot']
  }
];

export function BotChallengeSystem({ userProgress, onShowBotChallenge, onStartHatchEvolution }: BotChallengeSystemProps) {
  const navigate = useNavigate();
  
  // Mock progress for demonstration
  const progress = userProgress || {
    botsDefeated: 2,
    totalBots: 8,
    currentBotLevel: 1,
    hatchGamesPlayed: 3
  };

  const progressPercentage = (progress.botsDefeated / progress.totalBots) * 100;

  const handleStepClick = (step: typeof botChallengeSteps[0]) => {
    if (step.action === 'bot-challenge' && onShowBotChallenge) {
      onShowBotChallenge(step.bots);
    } else if (step.action === 'hatch-evolution' && onStartHatchEvolution) {
      onStartHatchEvolution();
    }
  };

  return (
    <div className="space-y-8">
      {/* Progress Overview */}
      <Card className="bg-gradient-to-r from-teen-green/10 via-teen-blue/10 to-teen-purple/10 border-teen-green/30">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-teen-green/20 rounded-full">
              <Target className="w-8 h-8 text-teen-green" />
            </div>
          </div>
          <CardTitle className="text-2xl bg-gradient-to-r from-teen-green to-teen-purple bg-clip-text text-transparent">
            Your Bot Challenge Journey! 🚀
          </CardTitle>
          <CardDescription className="text-base">
            Climb the ladder and become the ultimate bot challenger! ✨
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center space-y-2">
            <div className="text-3xl font-bold text-teen-green">{progress.botsDefeated}/{progress.totalBots}</div>
            <p className="text-sm text-muted-foreground">Bots Defeated</p>
            <Progress value={progressPercentage} className="w-full h-3" aria-label={`Bot challenge progress: ${progress.botsDefeated} out of ${progress.totalBots} bots defeated`} />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="text-center p-3 bg-teen-blue/10 rounded-lg border border-teen-blue/20">
              <div className="text-2xl font-bold text-teen-blue">{progress.hatchGamesPlayed}</div>
              <p className="text-xs text-muted-foreground">Hatch Games Played ⚡</p>
            </div>
            <div className="text-center p-3 bg-teen-purple/10 rounded-lg border border-teen-purple/20">
              <div className="text-2xl font-bold text-teen-purple">Level {progress.currentBotLevel}</div>
              <p className="text-xs text-muted-foreground">Bot Challenge Level 📈</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bot Challenge Steps */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-center mb-6">
          Choose Your Next Challenge! 🎯
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {botChallengeSteps.map((step, index) => {
            const isUnlocked = progress.currentBotLevel >= step.level;
            const isCompleted = progress.currentBotLevel > step.level;
            
            return (
              <Card 
                key={step.id}
                className={`
                  group cursor-pointer transition-all duration-300 hover:scale-105
                  ${step.borderColor} ${step.bgColor}
                  ${isUnlocked ? 'hover:shadow-teen-glow' : 'opacity-60 cursor-not-allowed'}
                  ${isCompleted ? 'bg-gradient-to-br from-teen-gold/20 to-teen-gold/10 border-teen-gold/50' : ''}
                `}
                onClick={() => isUnlocked && handleStepClick(step)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 ${isCompleted ? 'bg-teen-gold/20' : step.bgColor} rounded-full`}>
                        {isCompleted ? (
                          <Star className="w-6 h-6 text-teen-gold" />
                        ) : (
                          <step.icon className={`w-6 h-6 ${isCompleted ? 'text-teen-gold' : step.color}`} />
                        )}
                      </div>
                      <div>
                        <CardTitle className={`text-lg ${isCompleted ? 'text-teen-gold' : step.color}`}>
                          {step.title} {step.emoji}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant={isCompleted ? "default" : "secondary"} 
                            className={`text-xs ${
                              isCompleted 
                                ? 'bg-teen-gold/20 text-teen-gold border-teen-gold/30' 
                                : `${step.color} bg-transparent border-current`
                            }`}
                          >
                            Level {step.level}
                          </Badge>
                          {isCompleted && (
                            <Badge variant="outline" className="text-xs bg-teen-gold/10 text-teen-gold border-teen-gold/30">
                              Complete! ✅
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {isUnlocked && (
                      <ChevronRight className={`w-5 h-5 ${step.color} group-hover:translate-x-1 transition-transform duration-300`} />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="mb-3 text-muted-foreground">
                    {step.description}
                  </CardDescription>
                  {step.bots && (
                    <div className="text-xs text-muted-foreground mb-2">
                      🤖 Available bots: {step.bots.join(', ')}
                    </div>
                  )}
                  {isCompleted && (
                    <div className="text-xs text-teen-gold font-medium bg-teen-gold/10 p-2 rounded border border-teen-gold/20">
                      🎉 Reward Earned: {step.reward}
                    </div>
                  )}
                  {!isUnlocked && (
                    <div className="text-xs text-muted-foreground bg-muted/20 p-2 rounded">
                      🔒 Complete Level {step.level - 1} challenges to unlock!
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Quick Action */}
      <Card className="text-center bg-gradient-to-r from-teen-green/5 to-teen-blue/5 border-teen-green/20">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="text-4xl">🎯</div>
            <h4 className="text-lg font-bold text-foreground">Ready for a Quick Challenge?</h4>
            <p className="text-sm text-muted-foreground">
              Jump into a bot battle or try Hatch Evolution mode!
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => navigate('/leaderboards')}
                className="bg-teen-gold hover:bg-teen-gold/90 text-white"
              >
                View Leaderboards! 🏆
              </Button>
              <Button 
                onClick={() => handleStepClick(botChallengeSteps[0])}
                variant="outline"
                className="border-teen-green text-teen-green hover:bg-teen-green/10"
              >
                <Bot className="w-4 h-4 mr-2" />
                Challenge Bots! 🤖
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}